// app/hooks/useChat.js
//
// React hook layer for the cross-role chat (Professional ↔ Business).
// Wraps /api/messages endpoints and adds polling, optimistic send, and
// "scoped to focused screen" lifecycle so unmounted screens stop polling.
//
// Exports:
//   useConversations({ pollMs })            → { conversations, loading, error, refresh, role }
//   useMessages(conversationId, { pollMs }) → { messages, loading, error, send, markRead, refresh }
//   useRecipientSearch(query)               → { recipients, loading, error }

import { useState, useEffect, useCallback, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─────────────────────────────────────────────────────────────────────────────
// Shared fetch helper. Pulls the bearer token from any of the three valid
// AsyncStorage keys (per CLAUDE.md). Never falls back to a placeholder token.
// ─────────────────────────────────────────────────────────────────────────────
async function authedFetch(path, opts = {}) {
  let token = '';
  try {
    token = await AsyncStorage.getItem('auth_token')
         || await AsyncStorage.getItem('userToken')
         || await AsyncStorage.getItem('token')
         || '';
  } catch {}

  const headers = {
    'X-API-Key': API_KEY,
    'Authorization': token ? `Bearer ${token}` : undefined,
    ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
    ...(opts.headers || {}),
  };
  Object.keys(headers).forEach(k => headers[k] === undefined && delete headers[k]);

  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const text = await res.text();
  let data; try { data = JSON.parse(text); } catch { data = { raw: text }; }
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// useConversations — the inbox list
// ─────────────────────────────────────────────────────────────────────────────
export function useConversations(opts = {}) {
  const { pollMs = 5000 } = opts;
  const [conversations, setConversations] = useState([]);
  const [role,    setRole]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const mounted = useRef(true);
  const timer   = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await authedFetch('/api/messages/conversations');
      if (!mounted.current) return;
      setConversations(Array.isArray(r.conversations) ? r.conversations : []);
      setRole(r.role || null);
      setError(null);
    } catch (e) {
      if (mounted.current) setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    if (pollMs > 0) {
      timer.current = setInterval(refresh, pollMs);
    }
    return () => {
      mounted.current = false;
      if (timer.current) clearInterval(timer.current);
    };
  }, [pollMs, refresh]);

  return { conversations, role, loading, error, refresh };
}

// ─────────────────────────────────────────────────────────────────────────────
// useMessages — a single thread
// ─────────────────────────────────────────────────────────────────────────────
export function useMessages(conversationId, opts = {}) {
  const { pollMs = 3000, autoMarkRead = true } = opts;
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const mounted = useRef(true);
  const timer   = useRef(null);
  const lastIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!conversationId) { setLoading(false); return; }
    try {
      const r = await authedFetch(`/api/messages/conversations/${conversationId}/messages`);
      if (!mounted.current) return;
      const list = Array.isArray(r.messages) ? r.messages : [];
      setMessages(list);
      const lastId = list.length ? list[list.length - 1].id : 0;
      lastIdRef.current = lastId;
      setError(null);
    } catch (e) {
      if (mounted.current) setError(e);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [conversationId]);

  const send = useCallback(async (body, attachments = []) => {
    if (!conversationId || !body || !body.trim()) return null;
    // Optimistic append — replaced on next refresh
    const optimistic = {
      id: `tmp_${Date.now()}`,
      conversation_id: conversationId,
      sender_role: '__me',                       // marker; the screen treats "__me" as my own bubble
      sender_user_id: 0,
      body: body.trim(),
      attachments,
      read_at: null,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      const r = await authedFetch(`/api/messages/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: body.trim(), attachments }),
      });
      // Replace optimistic with the real row
      setMessages(prev => prev.map(m =>
        m.id === optimistic.id ? { ...r.message, sender_role: r.message.sender_role } : m
      ));
      return r.message;
    } catch (e) {
      setMessages(prev => prev.map(m =>
        m.id === optimistic.id ? { ...m, _failed: true, _pending: false } : m
      ));
      throw e;
    }
  }, [conversationId]);

  const markRead = useCallback(async () => {
    if (!conversationId) return;
    try {
      await authedFetch(`/api/messages/conversations/${conversationId}/read`, { method: 'POST', body: JSON.stringify({}) });
    } catch {}
  }, [conversationId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    if (autoMarkRead) markRead();
    if (pollMs > 0) {
      timer.current = setInterval(refresh, pollMs);
    }
    return () => {
      mounted.current = false;
      if (timer.current) clearInterval(timer.current);
    };
  }, [conversationId, pollMs, autoMarkRead, refresh, markRead]);

  return { messages, loading, error, send, markRead, refresh };
}

// ─────────────────────────────────────────────────────────────────────────────
// useRecipientSearch — for the New Message recipient picker
// ─────────────────────────────────────────────────────────────────────────────
export function useRecipientSearch(query, opts = {}) {
  const { companyId = null, debounceMs = 250 } = opts;
  const [recipients, setRecipients] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (query) qs.set('q', query);
        if (companyId) qs.set('company_id', String(companyId));
        const r = await authedFetch(`/api/messages/recipients/search?${qs.toString()}`);
        if (!mounted.current) return;
        setRecipients(Array.isArray(r.recipients) ? r.recipients : []);
        setError(null);
      } catch (e) {
        if (mounted.current) setError(e);
      } finally {
        if (mounted.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(t);
  }, [query, companyId, debounceMs]);

  return { recipients, loading, error };
}

// Helper for screens to call /api/messages/conversations/start
export async function startConversation({ recipient_role, recipient_user_id, company_id, subject, initial_message }) {
  return authedFetch('/api/messages/conversations/start', {
    method: 'POST',
    body: JSON.stringify({ recipient_role, recipient_user_id, company_id, subject, initial_message }),
  });
}

export default { useConversations, useMessages, useRecipientSearch, startConversation };
